import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { Subscription } from 'rxjs';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { AssistantService } from '@/cloud/services/assistant.service';
import { ActionBlock, AssistantPendingAction, PendingActionStatus } from '@/cloud/models/assistant.models';
import { DeviceAction } from 'src/api/device-action';
import { HealthDotComponent } from '@/shared/ui/health-dot/health-dot.component';
import { StatusDescriptor } from '@/shared/ui/status.model';
import { deriveDeviceStatus } from '@/cloud/models/device-status';
import { TranslationService } from '@/layout/service/translation.service';
import { fill } from '@/layout/service/assistant.translations';
import { clock } from '../assistant-format';

/** How long the confirm button has to be held. */
export const HOLD_MS = 1000;
/** Poll for the device action's progress in case a websocket update is missed. */
export const ACTION_POLL_MS = 5000;
export const ACTION_POLL_LIMIT_MS = 10 * 60 * 1000;

type Stage = 'queued' | 'dispatched' | 'succeeded' | 'actionFailed' | 'cancelled';

/**
 * The confirm card: what will happen, why, a hold-to-confirm button and a countdown to when the proposal
 * expires. Once confirmed it becomes a tracker that follows the device action (and the device's status dot)
 * live over the existing websocket topics.
 */
@Component({
    selector: 'app-assistant-confirm-action',
    standalone: true,
    imports: [HealthDotComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <section class="confirm-card" [attr.data-status]="state()" role="group" [attr.aria-label]="heading()">
            <header>
                <span class="confirm-icon"><i [class]="icon()" aria-hidden="true"></i></span>
                <span class="confirm-heading">{{ heading() }}</span>
                @if (deviceDot(); as dot) {
                    <app-health-dot [status]="dot" [pulse]="dot.key === 'online'" [label]="action().deviceName" />
                }
            </header>
            @if (action().reason) {
                <p class="confirm-reason">{{ action().reason }}</p>
            }

            @switch (state()) {
                @case ('Proposed') {
                    <p class="muted small">{{ t()('assistant.action.nothingRuns') }}</p>
                    <div class="confirm-buttons">
                        <button
                            type="button"
                            class="hold-button"
                            [class.holding]="holding()"
                            [disabled]="busy()"
                            (pointerdown)="startHold($event)"
                            (pointerup)="cancelHold()"
                            (pointerleave)="cancelHold()"
                            (pointercancel)="cancelHold()"
                            (keydown)="onKeyDown($event)"
                            (keyup)="onKeyUp($event)"
                            [attr.aria-label]="t()('assistant.action.hold') + ': ' + heading()"
                        >
                            <span class="hold-fill" [style.transition-duration.ms]="holding() ? holdMs : 150"></span>
                            <span class="hold-label"><i class="pi pi-check" aria-hidden="true"></i>{{ holding() ? t()('assistant.action.holding') : t()('assistant.action.hold') }}</span>
                        </button>
                        <button type="button" class="reject-button" [disabled]="busy()" (click)="reject()">{{ t()('assistant.action.reject') }}</button>
                        <span class="countdown muted" aria-live="off"><i class="pi pi-hourglass" aria-hidden="true"></i>{{ remaining() }}</span>
                    </div>
                }
                @case ('Confirmed') {
                    <ol class="tracker" aria-live="polite">
                        @for (s of stages; track s) {
                            <li [class.done]="stageIndex() > $index || (stage() === 'succeeded' && $index === 2)" [class.current]="stageIndex() === $index && stage() !== 'succeeded'" [class.failed]="failedStage() && $index === stageIndex()">
                                <span class="step-dot"></span>{{ t()('assistant.action.' + s) }}
                            </li>
                        }
                    </ol>
                    @if (stage() === 'actionFailed' || stage() === 'cancelled') {
                        <p class="confirm-error">{{ t()('assistant.action.' + stage()) }}{{ deviceAction()?.resultError ? ': ' + deviceAction()?.resultError : '' }}</p>
                    }
                }
                @case ('Rejected') {
                    <p class="muted small">{{ t()('assistant.action.rejected') }}</p>
                }
                @case ('Expired') {
                    <p class="muted small">{{ t()('assistant.action.expired') }}</p>
                }
                @case ('Failed') {
                    <p class="confirm-error">{{ failText() }}</p>
                }
            }
            @if (error()) {
                <p class="confirm-error" role="alert">{{ error() }}</p>
            }
        </section>
    `,
    styleUrl: './blocks.scss'
})
export class AssistantConfirmActionComponent {
    readonly block = input.required<ActionBlock>();

    private assistant = inject(AssistantService);
    private api = inject(CloudApiService);
    private destroyRef = inject(DestroyRef);
    readonly t = inject(TranslationService).t;

    readonly holdMs = HOLD_MS;
    readonly stages: Stage[] = ['queued', 'dispatched', 'succeeded'];
    readonly holding = signal(false);
    readonly busy = signal(false);
    readonly error = signal<string | null>(null);
    readonly now = signal(Date.now());
    readonly deviceAction = signal<DeviceAction | null>(null);
    private readonly device = signal<{ status?: number; lastCheckInUtc?: string } | null>(null);
    private holdTimer: ReturnType<typeof setTimeout> | null = null;
    private tracking: string | null = null;
    private subs: Subscription[] = [];
    private poll: ReturnType<typeof setInterval> | null = null;

    readonly action = computed<AssistantPendingAction>(() => this.assistant.actions()[this.block().action.id] ?? this.block().action);

    readonly state = computed<PendingActionStatus>(() => {
        const a = this.action();
        if (a.status === 'Proposed' && Date.parse(a.expiresUtc) <= this.now()) return 'Expired';
        // Being queued by a confirm (perhaps from another tab) looks like the first step of the tracker.
        return a.status === 'Confirming' ? 'Confirmed' : a.status;
    });

    readonly remaining = computed(() => fill(this.t()('assistant.action.expiresIn'), { time: clock(Date.parse(this.action().expiresUtc) - this.now()) }));

    readonly heading = computed(() => {
        const a = this.action();
        const key = a.action === 'Restart' ? 'assistant.action.restart' : a.action === 'Cancel' ? 'assistant.action.cancel' : 'assistant.action.reboot';
        return fill(this.t()(key), { device: a.deviceName });
    });

    readonly icon = computed(() => {
        switch (this.state()) {
            case 'Confirmed':
                return this.stage() === 'succeeded' ? 'pi pi-check-circle' : 'pi pi-spin pi-spinner';
            case 'Rejected':
            case 'Expired':
                return 'pi pi-minus-circle';
            case 'Failed':
                return 'pi pi-times-circle';
            default:
                return this.action().action === 'Cancel' ? 'pi pi-ban' : 'pi pi-power-off';
        }
    });

    readonly stage = computed<Stage>(() => {
        const s = this.deviceAction()?.status;
        if (s === 'Succeeded') return 'succeeded';
        if (s === 'Failed') return 'actionFailed';
        if (s === 'Cancelled') return 'cancelled';
        if (s === 'Dispatched') return 'dispatched';
        // A confirmed cancel has nothing further to follow.
        if (this.action().action === 'Cancel' && this.state() === 'Confirmed') return 'succeeded';
        return 'queued';
    });

    readonly stageIndex = computed(() => {
        const s = this.stage();
        return s === 'queued' ? 0 : s === 'dispatched' ? 1 : 2;
    });

    readonly failedStage = computed(() => this.stage() === 'actionFailed' || this.stage() === 'cancelled');

    readonly deviceDot = computed<StatusDescriptor | null>(() => {
        const d = this.device();
        return d?.status !== undefined ? deriveDeviceStatus(d, this.now()) : null;
    });

    readonly failText = computed(() => fill(this.t()('assistant.action.failed'), { error: this.action().error ?? '—' }));

    constructor() {
        const tick = setInterval(() => this.now.set(Date.now()), 1000);
        this.destroyRef.onDestroy(() => {
            clearInterval(tick);
            this.stopPolling();
            this.clearHold();
            this.subs.forEach((s) => s.unsubscribe());
        });

        // Follow the device action once there is one.
        effect(() => {
            const a = this.action();
            untracked(() => {
                if (a.status !== 'Confirmed' || !a.deviceActionId || this.tracking === a.deviceActionId) return;
                this.track(a);
            });
        });
    }

    startHold(event?: Event): void {
        if (this.busy() || this.state() !== 'Proposed') return;
        event?.preventDefault();
        this.clearHold();
        this.holding.set(true);
        this.holdTimer = setTimeout(() => {
            this.holdTimer = null;
            this.holding.set(false);
            this.confirm();
        }, HOLD_MS);
    }

    cancelHold(): void {
        if (!this.holdTimer) return;
        this.clearHold();
    }

    onKeyDown(event: KeyboardEvent): void {
        if ((event.key === 'Enter' || event.key === ' ') && !event.repeat) this.startHold(event);
    }

    onKeyUp(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ' ') this.cancelHold();
    }

    async confirm(): Promise<void> {
        this.busy.set(true);
        this.error.set(null);
        try {
            await this.assistant.decide(this.action().id, 'confirm');
        } catch (err) {
            this.error.set(AssistantConfirmActionComponent.message(err) ?? 'Could not confirm. Try again.');
        } finally {
            this.busy.set(false);
        }
    }

    async reject(): Promise<void> {
        this.clearHold();
        this.busy.set(true);
        this.error.set(null);
        try {
            await this.assistant.decide(this.action().id, 'reject');
        } catch (err) {
            this.error.set(AssistantConfirmActionComponent.message(err) ?? 'Could not decline. Try again.');
        } finally {
            this.busy.set(false);
        }
    }

    private track(a: AssistantPendingAction): void {
        this.tracking = a.deviceActionId;
        const id = a.deviceActionId!;
        this.subs.push(
            this.api.subscribeToDeviceActions(a.deviceId).subscribe((list) => {
                const found = list.find((x) => x.id === id);
                if (found) this.deviceAction.set(found);
            }),
            this.api.subscribeToDevice(a.deviceId).subscribe((d) => {
                if (d) this.device.set({ status: d.status, lastCheckInUtc: d.lastCheckInUtc });
            })
        );
        this.api.getDeviceActions(a.deviceId, { limit: 20 }).catch(() => undefined);

        // The websocket normally carries progress; polling covers a missed event or a dropped socket.
        const started = Date.now();
        this.stopPolling();
        this.poll = setInterval(() => {
            const status = this.deviceAction()?.status;
            if (status === 'Succeeded' || status === 'Failed' || status === 'Cancelled' || Date.now() - started > ACTION_POLL_LIMIT_MS) {
                this.stopPolling();
                return;
            }
            this.api.getDeviceActions(a.deviceId, { limit: 20 }).catch(() => undefined);
        }, ACTION_POLL_MS);
    }

    private stopPolling(): void {
        if (this.poll) clearInterval(this.poll);
        this.poll = null;
    }

    private clearHold(): void {
        if (this.holdTimer) clearTimeout(this.holdTimer);
        this.holdTimer = null;
        this.holding.set(false);
    }

    private static message(err: unknown): string | null {
        const e = err as { error?: { error?: string; message?: string } | string } | null;
        if (typeof e?.error === 'string') return e.error;
        return e?.error?.error ?? e?.error?.message ?? null;
    }
}
